import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import accountLinksToModelTransformer from 'src/Libs/installation-rights-management/transformer/account-links-to-model.transformer'
import { ChangeCompanyParams, CreateAccountLinkParams, UpdateAccountLinkStartAtParams } from './types'
import accountLinkToModelTransformer from 'src/Libs/installation-rights-management/transformer/account-link-to-model.transformer'
import { AccountLinkDto, AccountLinkListDto } from 'src/Libs/installation-rights-management/dto/account-links.dto'
import { AccountType } from 'src/Libs/installation-rights-management/types'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { handleFetchError } from 'src/Libs/api/utils'

export const createAccountLink = async ({ authFetch, data, installationId }: CreateAccountLinkParams): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `installations/${installationId}/account-links`,
    method: 'POST',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload: data,
  })

  if (response.status === StatusCodes.NO_CONTENT || response.status === StatusCodes.CREATED) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

export const getAccountLinkList = async (
  installationId: string,
  authFetch: AuthFetchWithResponse,
  accountType?: AccountType
): Promise<ApiResult<AccountLinkModel[]>> => {
  const fetchProps = {
    path: `installations/${installationId}/account-links`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload: {
      filter: accountType ? `accountType=${accountType}` : null,
    },
  }

  const response = await authFetch<AccountLinkListDto>(fetchProps)

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<AccountLinkModel[]>(null, false)
  }

  if (response.status === StatusCodes.OK) {
    const data = accountLinksToModelTransformer(response.data)

    return new ApiResult<AccountLinkModel[]>(data, false)
  }

  return new ApiResult<AccountLinkModel[]>(null, true)
}

export const getAccountLinkByAccountId = async (
  installationId: string,
  accountId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<AccountLinkModel>> => {
  const fetchProps = {
    path: `installations/${installationId}/account-links/${accountId}`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  }

  const response = await authFetch<AccountLinkDto>(fetchProps)

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<AccountLinkModel>(null, false)
  }

  if (response.status === StatusCodes.OK) {
    const data = accountLinkToModelTransformer(response.data)

    return new ApiResult<AccountLinkModel>(data, false)
  }

  return new ApiResult<AccountLinkModel>(null, true)
}

export const changeCompany = async ({ authFetch, data, installationId, accountLinkId }: ChangeCompanyParams): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `installations/${installationId}/account-links/${accountLinkId}/change-company`,
    method: 'PUT',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload: data,
  })

  if (response.status === StatusCodes.NO_CONTENT || response.status === StatusCodes.OK) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}

export const updateAccountLinkStartAt = async ({
  authFetch,
  installationId,
  accountLinkId,
  startAt,
  reason,
}: UpdateAccountLinkStartAtParams): Promise<ApiResult<void, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<void>({
      path: `installations/${installationId}/account-links/${accountLinkId}`,
      method: 'PUT',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload: { startAt, reason },
    })

    if (response.status === StatusCodes.NO_CONTENT || response.status === StatusCodes.OK) {
      return new ApiResult<void, AuthFetchError | Error>(null, false)
    }

    return new ApiResult<void, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const deleteAccountLink = async (installationId: number, accountId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/account-links/${accountId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}
